/**
 * SavedFlowsPanel Component
 *
 * Sidebar panel for browsing and loading saved workflow flows.
 * Shows list of saved flows with status indicators and actions.
 */

import { useState, useEffect } from 'react'
import {
  FolderOpen, Loader2, AlertCircle, Trash2, CheckCircle,
  XCircle, Clock, Play, ChevronDown,
} from 'lucide-react'
import { listFlows, deleteFlow, getFlow } from '../../services/flowService'
import './SavedFlowsPanel.css'

function SavedFlowsPanel({ onLoadFlow, currentAccountId, refreshTrigger }) {
  const [flows, setFlows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [collapsed, setCollapsed] = useState(false)

  // Load saved flows
  const loadFlows = async () => {
    console.log(' SavedFlowsPanel: Loading flows...', { currentAccountId })
    setLoading(true)
    setError(null)

    try {
      const response = await listFlows(currentAccountId || null)
      console.log(' SavedFlowsPanel: Loaded flows:', response)
      setFlows(response.flows || [])
    } catch (err) {
      console.error(' SavedFlowsPanel: Failed to load flows:', err)
    } finally {
      setLoading(false)
    }
  }

  // Load flows on mount, when account changes, or when refreshTrigger changes
  useEffect(() => {
    loadFlows()
  }, [currentAccountId, refreshTrigger])

  // Force refresh on component mount (handles hot reload)
  useEffect(() => {
    loadFlows()
  }, [])

  // Handle flow load
  const handleLoadFlow = async (flowId) => {
    try {
      const flowDetail = await getFlow(flowId)
      console.log('Loaded flow:', flowDetail)

      if (onLoadFlow) {
        onLoadFlow(flowDetail)
      }
    } catch (err) {
      console.error('Failed to load flow:', err)
      alert(`Failed to load flow: ${err.message}`)
    }
  }

  // Handle flow delete
  const handleDeleteFlow = async (flowId, flowName, e) => {
    e.stopPropagation() // Prevent loading flow when deleting

    if (!confirm(`Delete flow "${flowName}"?`)) {
      return
    }

    setDeleting(flowId)
    try {
      await deleteFlow(flowId)
      console.log('Deleted flow:', flowId)

      // Refresh list
      await loadFlows()
    } catch (err) {
      console.error('Failed to delete flow:', err)
      alert(`Failed to delete flow: ${err.message}`)
    } finally {
      setDeleting(null)
    }
  }

  // Get status class for dot
  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'completed'
      case 'failed': return 'failed'
      case 'running': return 'running'
      default: return 'not-run'
    }
  }

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return date.toLocaleDateString()
  }

  return (
    <div className={`saved-flows-panel ${collapsed ? 'collapsed' : ''}`}>
      <button className="panel-header" onClick={() => setCollapsed(!collapsed)}>
        <ChevronDown size={16} className="chevron-icon" />
        <FolderOpen size={16} className="folder-icon" />
        <h3>Saved Flows</h3>
        <span className="flow-count">{flows.length}</span>
      </button>

      <div className="panel-content-wrapper">
        {loading ? (
          <div className="panel-loading">
            <Loader2 size={20} className="spin" />
            <span>Loading flows...</span>
          </div>
        ) : error ? (
          <div className="panel-error">
            <AlertCircle size={20} />
            <span>{error}</span>
            <button onClick={loadFlows}>Retry</button>
          </div>
        ) : flows.length === 0 ? (
          <div className="panel-empty">
            <FolderOpen size={32} />
            <p>No saved flows yet</p>
            <span className="hint">Save your workflow to access it later</span>
          </div>
        ) : (
          <div className="flows-list">
            {flows.map((flow) => (
              <div
                key={flow.id}
                className="flow-item"
                onClick={() => handleLoadFlow(flow.id)}
                title={`Load ${flow.name}`}
              >
                {/* Header: Name + Delete */}
                <div className="flow-header">
                  <span className="flow-name">{flow.name}</span>
                  <button
                    className="delete-btn"
                    onClick={(e) => handleDeleteFlow(flow.id, flow.name, e)}
                    disabled={deleting === flow.id}
                    title="Delete flow"
                  >
                    {deleting === flow.id ? (
                      <Loader2 size={10} className="spin" />
                    ) : (
                      <Trash2 size={10} />
                    )}
                  </button>
                </div>

                {/* Meta: Status + Nodes + Date */}
                <div className="flow-meta">
                  <span className={`status-dot ${getStatusClass(flow.status)}`} />
                  <span className="status-text">{flow.status}</span>
                  <span className="separator">•</span>
                  <span className="node-count">{flow.node_count} nodes</span>
                  <span className="separator">•</span>
                  <span>{formatDate(flow.updated_at)}</span>
                </div>

                {/* Footer: Account info */}
                <div className="flow-footer">
                  <span className="flow-account">{flow.account_id} › {flow.application_name}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default SavedFlowsPanel
